import React, { useRef, useState } from 'react';


interface ImageUploadProps {
  value: string | null;
  onChange: (url: string | null) => void;
  disabled?: boolean;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ value, onChange, disabled }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(value ?? null);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) { setError('Nur Bilddateien erlaubt (JPEG, PNG, WebP)'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Datei zu groß (max. 10 MB)'); return; }

    setUploading(true);
    setError(null);

    try {
      const base64Url = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_SIZE = 800;
            let width = img.width;
            let height = img.height;

            if (width > height && width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            } else if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.8));
          };
          img.onerror = () => reject(new Error('Fehler beim Laden des Bildes'));
          img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Fehler beim Lesen der Datei'));
        reader.readAsDataURL(file);
      });

      onChange(base64Url);
      setPreview(base64Url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verarbeitung fehlgeschlagen');
      setPreview(null);
      onChange(null);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => { const f = e.target.files?.[0]; if (f) handleFile(f); };
  const clear = () => { setPreview(null); onChange(null); setError(null); if (inputRef.current) inputRef.current.value = ''; };

  return (
    <div className="flex flex-col gap-2">
      <input ref={inputRef} type="file" accept="image/*" className="hidden"
        onChange={handleChange} disabled={disabled || uploading} id="image-upload-input" />

      {preview ? (
        <div className="relative rounded-xl overflow-hidden border border-border group shadow-sm">
          <img src={preview} alt="Vorschau" className="w-full h-48 object-cover" />
          {uploading && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center backdrop-blur-sm">
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-2 border-sage border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-cream font-medium">Wird hochgeladen…</span>
              </div>
            </div>
          )}
          {!uploading && (
            <div className="absolute inset-0 bg-white/75 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 backdrop-blur-[2px]">
              <button type="button" onClick={() => inputRef.current?.click()} className="btn-ghost text-xs shadow-sm">
                Ändern
              </button>
              <button type="button" onClick={clear} className="btn-danger text-xs">
                Entfernen
              </button>
            </div>
          )}
        </div>
      ) : (
        <label
          htmlFor="image-upload-input"
          className={`flex flex-col items-center justify-center gap-3 h-40 rounded-xl border-2 border-dashed
            bg-paper transition-colors duration-200
            ${uploading ? 'border-sage/40 cursor-wait' : 'border-border hover:border-sage/50 cursor-pointer hover:bg-sage/5'}`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          {uploading ? (
            <>
              <div className="w-8 h-8 border-2 border-sage border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-creamDim">Wird hochgeladen…</span>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center border border-sage/20">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.5" className="text-sage">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="17 8 12 3 7 8"/>
                  <line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
              </div>
              <div className="text-center">
                <p className="text-sm text-creamDim">Bild hierher ziehen oder</p>
                <p className="text-sm text-sage font-medium">Datei auswählen</p>
              </div>
              <p className="text-xs text-creamDim/50">JPEG, PNG, WebP · max. 10 MB</p>
            </>
          )}
        </label>
      )}

      {error && (
        <p className="text-red-500 text-xs flex items-center gap-1.5 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};
